import { ToastStatusGlobal } from '@/components';
import BoxInfo from '@/components/boxInfo';
import IconBox from '@/components/iconBox';
import TextData from '@/components/textData';
import translate from '@/components/textData/utils';
import {
  initialStateUsageStatementProcess,
  TRANSLATED_USAGE_STATEMENT_PROCESS_STATUS,
} from '@/modules/copay/entities/constants/usageStatementProcess';
import { USAGE_STATEMENT_STATUS_FULL } from '@/modules/copay/entities/enums/usageStatement';
import { UsageStatementProcess } from '@/modules/copay/entities/interfaces/usageStatements';
import { useUsageStatementProcess } from '@/modules/copay/hooks/useUsageStatements/useUsageStatementProcess';
import useFileDownload from '@/modules/files/hooks/useFileDownload/useFileDownload';
import useCompany from '@/shared/hooks/useCompany/useCompany';
import ICONS from '@/shared/entities/constants/icons';
import { getFormattedDate } from '@/utils/format';
import React from 'react';
import { Button, Text } from 'vkit/lib/components';
import { Grid } from 'vkit/lib/context';

interface InfoCopayProps {
  usageStatementProcess?: UsageStatementProcess;
  companyParentId?: string;
  loading: boolean;
  refresh: () => void;
}

export const InfoCopay: React.FC<InfoCopayProps> = ({
  usageStatementProcess = initialStateUsageStatementProcess,
  companyParentId,
  loading,
  refresh,
}) => {
  const { toast } = ToastStatusGlobal();
  const { company } = useCompany();
  const { downloadFile, loading: downloading } = useFileDownload();
  const { loading: publishing, publish } = useUsageStatementProcess({
    callbackPublishSuccess: () => {
      refresh?.();
      toast(
        'Feito',
        `Arquivo enviado para pasta de faturamento da empresa ${company?.name}.`,
        'success',
      );
    },
  });

  const translatedUsageStatementCompetenceMonth = translate.month(
    String(usageStatementProcess.usageStatement?.competenceMonth),
  );

  return (
    <BoxInfo
      title='Resumo do arquivo'
      icon={ICONS.ALERT}
      header={
        <Grid gap={8}>
          <Button
            icon={ICONS.DOWNLOAD}
            label='Download da planilha original'
            solo
            outlined
            iconColor='colorDefault'
            loading={downloading}
            disabled={!usageStatementProcess.originalFileId}
            onClick={() => {
              downloadFile(usageStatementProcess.originalFileId, companyParentId);
            }}
          />
          <Button
            icon={ICONS.DOWNLOAD}
            label='Baixar resultado de coparticipação'
            loading={downloading}
            disabled={!usageStatementProcess.resultFileId}
            iconColor={usageStatementProcess.resultFileId ? 'clear' : 'light'}
            onClick={() => {
              usageStatementProcess.resultFileId &&
                downloadFile(usageStatementProcess.resultFileId, companyParentId);
            }}
            testId='result-file-download'
          />
        </Grid>
      }>
      <Grid column gap={16}>
        <TextData
          loading={loading}
          data={[
            {
              label: 'Nome do arquivo',
              text: usageStatementProcess.fileName,
              sizeDefault: 33.33,
              sizeMedium: 33.33,
            },
            {
              label: 'Mês da coparticipação',
              text: `${translatedUsageStatementCompetenceMonth} de ${usageStatementProcess.usageStatement?.competenceYear}`,
              sizeDefault: 33.33,
              sizeMedium: 33.33,
            },
            {
              label: 'Status',
              text: usageStatementProcess.processing
                ? 'Processando'
                : TRANSLATED_USAGE_STATEMENT_PROCESS_STATUS[usageStatementProcess.status],
              sizeDefault: 33.33,
              sizeMedium: 33.33,
            },
            {
              label: 'Criado por',
              text: usageStatementProcess.createdByUser?.name,
              sizeDefault: 50,
              sizeMedium: 50,
            },
            {
              label: 'Criado em',
              text: getFormattedDate(usageStatementProcess.createdAt, { showHours: true }),
              sizeDefault: 50,
              sizeMedium: 50,
            },
            {
              label: 'Aprovado por',
              text: usageStatementProcess.approveByUser?.name,
              sizeDefault: 50,
              sizeMedium: 50,
              hidden: !usageStatementProcess.approveAt,
            },
            {
              label: 'Aprovado em',
              text:
                usageStatementProcess.approveAt &&
                getFormattedDate(usageStatementProcess.approveAt, { showHours: true }),
              sizeDefault: 50,
              sizeMedium: 50,
              hidden: !usageStatementProcess.approveAt,
            },
            {
              label: 'Descrição adicionada por',
              text: usageStatementProcess.descriptionAddedByUser?.name,
              sizeDefault: 50,
              sizeMedium: 50,
              hidden: !usageStatementProcess.descriptionAddedAt,
            },
            {
              label: 'Descrição adicionada em',
              text:
                usageStatementProcess.descriptionAddedAt &&
                getFormattedDate(usageStatementProcess.descriptionAddedAt, { showHours: true }),
              sizeDefault: 50,
              sizeMedium: 50,
              hidden: !usageStatementProcess.descriptionAddedAt,
            },
            {
              label: 'Rótulo adicionado por',
              text: usageStatementProcess.labelAddedByUser?.name,
              sizeDefault: 50,
              sizeMedium: 50,
              hidden: !usageStatementProcess.labelAddedAt,
            },
            {
              label: 'Rótulo adicionado em',
              text:
                usageStatementProcess.labelAddedAt &&
                getFormattedDate(usageStatementProcess.labelAddedAt, { showHours: true }),
              sizeDefault: 50,
              sizeMedium: 50,
              hidden: !usageStatementProcess.labelAddedAt,
            },
          ]}
        />

        {[USAGE_STATEMENT_STATUS_FULL.PROCESSED, USAGE_STATEMENT_STATUS_FULL.FINISHED].includes(
          usageStatementProcess.status,
        ) && (
          <>
            <Grid alignContent='justify' alignItems='center' stretch>
              <Grid alignItems='center' gap={8}>
                <IconBox icon={ICONS.SEND} />
                <Text
                  color='colorDefault'
                  iconColor='colorDefault'
                  size='small'
                  title
                  value='Envio para faturamento'
                />
              </Grid>

              <Button
                icon={ICONS.FOLDER}
                label='Enviar para faturamento'
                loading={publishing}
                disabled={usageStatementProcess.status !== USAGE_STATEMENT_STATUS_FULL.PROCESSED}
                onClick={() => publish(usageStatementProcess.id)}
                iconColor={
                  usageStatementProcess.status !== USAGE_STATEMENT_STATUS_FULL.PROCESSED
                    ? 'light'
                    : 'clear'
                }
                iconDir='right'
                testId='sender'
              />
            </Grid>

            <TextData
              loading={loading}
              data={[
                {
                  label: 'Data do envio',
                  text:
                    usageStatementProcess.sharedAt &&
                    getFormattedDate(usageStatementProcess.sharedAt, { showHours: true }),
                  sizeDefault: 50,
                  sizeMedium: 50,
                },
                {
                  label: 'Enviado por',
                  text: usageStatementProcess.sharedByUser?.name,
                  sizeDefault: 50,
                  sizeMedium: 50,
                },
              ]}
            />
          </>
        )}
      </Grid>
    </BoxInfo>
  );
};
